import { Link } from "react-router-dom";
import { Printer, Download, ArrowLeft, Swords, CheckCircle2, XCircle, Minus } from "lucide-react";
import { IMAGES, COMPANY } from "../lib/data";

const COLS = ["Big Bad Mats", "Composite HDPE", "Timber / Crane", "Steel / Aluminum"];
// value tiers: "win" (flame), "ok", "bad"
const ROWS = [
  ["Unit weight (handling)", ["Light — machine-set", "Medium", "Very heavy — crane", "Heavy"], [1, 2, 3, 3]],
  ["Service life", ["10+ yrs", "3–5 yrs", "2–4 yrs", "8–10 yrs"], [1, 2, 3, 1]],
  ["Breakage / cracking", ["None — engineered", "Cracks under load", "Rots / splinters", "Bends / corrodes"], [1, 3, 3, 2]],
  ["Load distribution", ["High-psi, low-CBR", "Moderate", "High but rigid", "High"], [1, 2, 2, 1]],
  ["Contamination", ["Non-porous · zero-leach", "Semi-porous", "Absorbs fluids", "Non-porous"], [1, 2, 3, 1]],
  ["Install speed", ["Fast-connect lock", "Moderate", "Slow, crane-set", "Slow, bolted"], [1, 2, 3, 3]],
  ["Reusability / turns", ["Very high", "Moderate", "Low", "Moderate"], [1, 2, 3, 2]],
  ["Disposal / recyclable", ["100% recyclable", "Landfill fees", "Landfill / burn", "Scrap value"], [1, 3, 3, 2]],
  ["Cost per deployment", ["Lowest (TCO)", "High (re-buys)", "High (rot/loss)", "High (CapEx)"], [1, 3, 3, 2]],
];

const TierIcon = ({ tier }) =>
  tier === 1 ? <CheckCircle2 className="h-3.5 w-3.5 text-flame flex-shrink-0" />
  : tier === 3 ? <XCircle className="h-3.5 w-3.5 text-charcoal/40 flex-shrink-0" />
  : <Minus className="h-3.5 w-3.5 text-charcoal/40 flex-shrink-0" />;

export default function Battlecard() {
  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      <div className="sticky top-0 z-40 bg-ink text-white border-b border-white/10 print:hidden">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/products/big-bad-mats" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm"><ArrowLeft className="h-4 w-4" /> Back to product</Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-white/50">Competitive Battlecard · One-Page</span>
            <a href="/big-bad-mats-battlecard.pdf" download data-testid="download-battlecard" className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2 text-sm font-semibold hover:border-flame hover:text-flame transition-colors"><Download className="h-4 w-4" /> Download PDF</a>
            <button onClick={() => window.print()} data-testid="print-battlecard" className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors"><Printer className="h-4 w-4" /> Print</button>
          </div>
        </div>
      </div>

      <div className="py-8 print:py-0 flex justify-center">
        <div data-testid="battlecard-sheet" className="bg-white shadow-2xl print:shadow-none w-[297mm] h-[210mm] p-[11mm] flex flex-col" style={{ fontSize: "10px" }}>
          <header className="flex items-start justify-between border-b-2 border-ink pb-3">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
                <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVEST</span>
                <span className="ml-2 border border-flame/40 bg-flame/10 text-flame px-2 py-0.5 text-[8px] uppercase tracking-[0.18em]">Competitive Battlecard</span>
              </div>
              <h1 className="font-display font-black tracking-tight text-ink leading-[1] text-[32px]">Big Bad Mats vs. The Field.</h1>
              <p className="mt-2 text-charcoal text-[11px]">A head-to-head on the specs that drive <span className="font-semibold text-ink">total cost of ownership</span> — not just sticker price.</p>
            </div>
            <div className="flex items-center gap-2 text-flame"><Swords className="h-8 w-8" /></div>
          </header>

          <div className="flex-1 pt-4 flex flex-col min-h-0">
            <div className="grid grid-cols-[1.3fr_1.1fr_1fr_1fr_1fr] bg-ink text-white text-[9px] uppercase tracking-wider">
              <div className="px-3 py-2">Attribute</div>
              {COLS.map((c, i) => (
                <div key={c} className={`px-3 py-2 border-l border-white/10 font-display font-bold ${i === 0 ? "text-flame" : ""}`}>{c}</div>
              ))}
            </div>
            <div className="flex-1 grid" style={{ gridTemplateRows: `repeat(${ROWS.length}, 1fr)` }}>
              {ROWS.map(([attr, vals, tiers], ri) => (
                <div key={attr} className={`grid grid-cols-[1.3fr_1.1fr_1fr_1fr_1fr] items-center border-b border-border ${ri % 2 ? "bg-muted/40" : "bg-white"}`}>
                  <div className="px-3 py-1.5 font-medium text-ink text-[10.5px]">{attr}</div>
                  {vals.map((val, ci) => (
                    <div key={ci} className={`px-3 py-1.5 border-l border-border flex items-center gap-1.5 text-[10px] ${ci === 0 ? "bg-flame/5" : ""}`}>
                      <TierIcon tier={tiers[ci]} />
                      <span className={ci === 0 ? "text-ink font-semibold" : "text-charcoal"}>{val}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 bg-ink text-white px-5 py-3 flex items-center justify-between gap-4">
            <div>
              <div className="font-display font-extrabold text-[15px]">The bottom line: fewer assets, zero re-buys, lowest cost per deployment.</div>
              <div className="text-white/60 text-[10px] mt-0.5">{COMPANY.phone} · {COMPANY.email}</div>
            </div>
            <Link to="/big-bad-mats-roi" className="inline-flex items-center bg-flame text-white px-5 py-2.5 font-semibold text-[11px] hover:bg-flame/90 transition-colors print:hidden flex-shrink-0">See the ROI Model</Link>
          </div>
          <p className="mt-2 text-[8px] text-muted-foreground text-center">Comparative positioning is representative of typical heavy-industrial performance; validate against project specs. © {new Date().getFullYear()} {COMPANY.legal}.</p>
        </div>
      </div>
    </div>
  );
}
